import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/services/api';
import type {
  Period,
  PlausibleResponse,
  RealtimeResponse,
  OverviewMetrics,
  TimeseriesPoint,
  BreakdownRow,
  BreakdownProperty,
} from './types';

/* ── helpers ───────────────────────────────────────────────── */

function parseOverview(res: PlausibleResponse): OverviewMetrics {
  const m = res.results[0]?.metrics ?? [];
  const keys = res.query.metrics as string[];
  const idx = (k: string) => keys.indexOf(k);
  return {
    visitors: m[idx('visitors')] ?? 0,
    visits: m[idx('visits')] ?? 0,
    pageviews: m[idx('pageviews')] ?? 0,
    bounce_rate: m[idx('bounce_rate')] ?? 0,
    visit_duration: m[idx('visit_duration')] ?? 0,
    views_per_visit: m[idx('views_per_visit')] ?? 0,
  };
}

function parseTimeseries(res: PlausibleResponse): TimeseriesPoint[] {
  const keys = res.query.metrics as string[];
  const vi = keys.indexOf('visitors');
  const pi = keys.indexOf('pageviews');

  const labels = (res.meta as { time_labels?: string[] }).time_labels ?? [];
  const byDate = new Map<string, PlausibleResponse['results'][number]>();
  for (const r of res.results) if (r.dimensions[0]) byDate.set(r.dimensions[0], r);

  return labels.map((date) => {
    const r = byDate.get(date);
    return {
      date,
      visitors: r?.metrics[vi] ?? 0,
      pageviews: r?.metrics[pi] ?? 0,
    };
  });
}

function parseBreakdown(res: PlausibleResponse): BreakdownRow[] {
  const keys = res.query.metrics as string[];
  const vi = keys.indexOf('visitors');
  const bi = keys.indexOf('bounce_rate');
  return res.results.map((r) => ({
    dimension: r.dimensions[0] ?? '(unbekannt)',
    visitors: r.metrics[vi] ?? 0,
    bounce_rate: r.metrics[bi] ?? 0,
  }));
}

function intervalForPeriod(period: Period): string {
  if (period === 'day') return 'hour';
  return 'day';
}

/* ── hooks ─────────────────────────────────────────────────── */

export function useOverview(siteId: string, period: Period) {
  const [data, setData] = useState<OverviewMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<PlausibleResponse>(`/sites/${siteId}/analytics/overview?period=${period}`)
      .then((res) => { if (!cancelled) setData(parseOverview(res)); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Fehler'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [siteId, period]);

  return { data, loading, error };
}

export function useTimeseries(siteId: string, period: Period) {
  const [data, setData] = useState<TimeseriesPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const interval = intervalForPeriod(period);
    api
      .get<PlausibleResponse>(`/sites/${siteId}/analytics/timeseries?period=${period}&interval=${interval}`)
      .then((res) => { if (!cancelled) setData(parseTimeseries(res)); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Fehler'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [siteId, period]);

  return { data, loading, error };
}

export function useBreakdown(siteId: string, period: Period, property: BreakdownProperty) {
  const [data, setData] = useState<BreakdownRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<PlausibleResponse>(`/sites/${siteId}/analytics/breakdown?property=${property}&period=${period}&limit=10`)
      .then((res) => { if (!cancelled) setData(parseBreakdown(res)); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Fehler'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [siteId, period, property]);

  return { data, loading, error };
}

export function useRealtime(siteId: string) {
  const [visitors, setVisitors] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  const poll = useCallback(() => {
    api
      .get<RealtimeResponse>(`/sites/${siteId}/analytics/realtime`)
      .then((res) => setVisitors(res.visitors))
      .catch(() => { /* silent – indicator just stays stale */ });
  }, [siteId]);

  useEffect(() => {
    poll();
    timerRef.current = setInterval(poll, 30_000);
    return () => clearInterval(timerRef.current);
  }, [poll]);

  return visitors;
}
